import { Modal } from "@general-backoffice/core";
import cn from "classnames";
import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Badge, Button, Col, FormGroup, Label, Row, Spinner } from "reactstrap";

import leverageOptions from "./utils/leverageOptions";

interface LeverageSelectionInputProps {
  leverage?: number | null;
  onChange?: (maxLeverage: number, minLeverage: number) => void;
  required?: boolean;
  isLoading?: boolean;
  label?: React.ReactNode;
  [key: string]: unknown;
}

const LeverageSelectionInput = ({
  leverage,
  onChange,
  required,
  isLoading: isLoadingParent,
  ...props
}: LeverageSelectionInputProps) => {
  const { t } = useTranslation("utils", { keyPrefix: "leverages" });

  const currOption = useMemo(() => (leverage ? leverageOptions.find((o) => o?.value === leverage) : null), [leverage]);

  const areLoading = isLoadingParent;
  return (
    <div>
      <FormGroup row className="d-flex align-items-center justify-content-between no-gutters mb-2">
        <Label className="col-auto form-control-label my-0 mr-2 d-flex justify-content-between w-100">
          {props?.label || t("inputLabel")}
        </Label>
        {/* Add hidden input to control required */}
        <input
          style={{ height: 0, opacity: 0 }}
          name={typeof props?.label === "string" ? props.label : undefined}
          value={leverage ?? ""}
          required={required}
          readOnly
        />
      </FormGroup>

      {!areLoading ? (
        <Row className="mx--2">
          {leverageOptions?.map((option, i) => {
            const isSelected = option.value === currOption?.value;
            if (!onChange && !isSelected) return null;
            return (
              <Col key={i} xs={6} className="px-2 mb-2 transition">
                <LeverageCard
                  {...option}
                  isSelected={isSelected}
                  disabled={!onChange}
                  onClick={() => {
                    const reversed = [...option.leverageRange].reverse();
                    onChange?.(reversed[0], reversed[1]);
                  }}
                />
              </Col>
            );
          })}
        </Row>
      ) : (
        <Spinner color="primary" size="sm" />
      )}
    </div>
  );
};

export default LeverageSelectionInput;

interface LeverageCardProps {
  value: number;
  disabled: boolean;
  isSelected: boolean;
  onClick: () => void;
}

const LeverageCard = ({ value, disabled, isSelected, onClick }: LeverageCardProps) => {
  const { t } = useTranslation("utils", { keyPrefix: `leverages.${value}` });
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "h-100 d-flex flex-column text-left justify-content-between shadow-lg border px-2 py-2 w-100 position-relative overflow-hidden text-default",
        { "bg-info": isSelected }
      )}
      style={{ minHeight: 80 }}
    >
      <div>
        <div className={cn("font-weight-700", { "text-white": isSelected })} style={{ fontSize: "0.9rem" }}>
          {t("label")}
        </div>
        <Badge
          color={isSelected ? "success" : "info border text-success"}
          className={cn("rounded-pill ml--1 font-weight-400")}
          style={{ textTransform: "none", fontSize: "0.7rem", padding: "2px 6px" }}
        >
          {t("description")}
        </Badge>
      </div>
      <small
        className={cn({ "text-white": isSelected })}
        style={{ fontSize: ".55rem", maxWidth: 140, lineHeight: 1.2 }}
      >
        {t("regulation")}
      </small>
    </Button>
  );
};

export const LeverageInfoModal = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { t } = useTranslation("exchangeAccounts", { keyPrefix: "updateExchangeAccountModal.leverageInfoModal" });
  return (
    <Modal.Info titleInfo={t("title")} isOpen={isOpen} toggleOpen={close}>
      <Trans
        t={t}
        i18nKey="text"
        components={{
          h2: <h3 className="text-white" />,
          h3: <h4 className="text-white" />,
          ul: <ul />,
          li: <li />,
          strong: <strong className="font-weight-bold" />,
          table: <table border={1} className="text-xs border-white mb-3" />,
          thead: <thead />,
          tbody: <tbody />,
          tr: <tr />,
          td: <td />,
          th: <th />,
        }}
      />
    </Modal.Info>
  );
};
